interface MinifiedPayload {
  _minified: true;
  _version: 1;
  _keyMap: string[];
  _data: any;
}

/**
 * Minifies a JSON object by creating a key mapping to reduce payload size
 * Particularly useful for objects with repeated long property names
 */
const minifyJson = (obj: any): MinifiedPayload => {
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return {
      _minified: true,
      _version: 1,
      _keyMap: [],
      _data: obj,
    };
  }

  const keySet = new Set<string>();

  // Collect all unique keys recursively
  function collectKeys(item: any, visited = new Set()): void {
    if (item === null || item === undefined || typeof item !== 'object' || visited.has(item)) {
      return;
    }

    visited.add(item);

    if (Array.isArray(item)) {
      item.forEach(element => collectKeys(element, visited));
    } else {
      Object.keys(item).forEach(key => {
        keySet.add(key);
        collectKeys(item[key], visited);
      });
    }
  }

  collectKeys(obj);

  const keyMap = Array.from(keySet).sort(); // Sort for consistent mapping
  const keyToIndex = new Map(keyMap.map((key, index) => [key, index]));

  // Transform the object using key indices
  function transformObject(item: any, visited = new Set()): any {
    if (item === null || item === undefined || typeof item !== 'object' || visited.has(item)) {
      return item;
    }

    visited.add(item);

    if (Array.isArray(item)) {
      return item.map(element => transformObject(element, visited));
    }

    const transformed: Record<number, any> = {};
    Object.keys(item).forEach(key => {
      const keyIndex = keyToIndex.get(key);

      if (keyIndex !== undefined) {
        transformed[keyIndex] = transformObject(item[key], visited);
      }
    });

    return transformed;
  }

  return {
    _minified: true,
    _version: 1,
    _keyMap: keyMap,
    _data: transformObject(obj),
  };
};

/**
 * Expands a minified JSON object back to its original form
 */
const expandJson = (minified: MinifiedPayload | any): any => {
  // If it's not minified, return as-is
  if (!minified || typeof minified !== 'object' || !minified._minified) {
    return minified;
  }

  const { _keyMap, _data } = minified;

  if (!_keyMap || !Array.isArray(_keyMap)) {
    return _data;
  }

  // Transform the object back using the key map
  function restoreObject(item: any, visited = new Set()): any {
    if (item === null || item === undefined || typeof item !== 'object' || visited.has(item)) {
      return item;
    }

    visited.add(item);

    if (Array.isArray(item)) {
      return item.map(element => restoreObject(element, visited));
    }

    const restored: Record<string, any> = {};
    Object.keys(item).forEach(keyIndex => {
      const originalKey = _keyMap[parseInt(keyIndex, 10)];

      if (originalKey !== undefined) {
        restored[originalKey] = restoreObject(item[keyIndex], visited);
      }
    });

    return restored;
  }

  return restoreObject(_data);
};

/**
 * Calculates the compression ratio of minification
 */
const getCompressionRatio = (original: any, minified: MinifiedPayload): number => {
  const originalSize = JSON.stringify(original).length;
  const minifiedSize = JSON.stringify(minified).length;

  return originalSize > 0 ? (1 - minifiedSize / originalSize) * 100 : 0;
};

/**
 * Utility function to check if an object is minified
 */
const isMinified = (obj: any): obj is MinifiedPayload => {
  return obj && typeof obj === 'object' && obj._minified === true;
};

/**
 * Auto-minifies if the payload is large enough to benefit from minification
 * Returns original object if minification doesn't provide significant benefit
 */
const autoMinifyJson = (obj: any, minSizeThreshold = 1000, minCompressionThreshold = 10): any => {
  const originalSize = JSON.stringify(obj).length;

  if (originalSize < minSizeThreshold) {
    return obj;
  }

  const minified = minifyJson(obj);
  const compressionRatio = getCompressionRatio(obj, minified);

  return compressionRatio >= minCompressionThreshold ? minified : obj;
};

export { autoMinifyJson, expandJson, isMinified, minifyJson };
